/**
 * The Machine Board (#492): one computer, opened up.
 *
 * The Engine Room says how hard each machine is working. The Machine Board
 * answers the next question - what is actually on this one - and it answers it
 * from the same rows the rest of the desk already reads:
 *
 *   node  ->  Workers  ->  terminal / session  ->  runtime / model  ->  task / project
 *                                                                    ->  capacity
 *
 * It is a projection and nothing else. There is no Worker table, terminal
 * registry, runtime registry, task ledger or capacity store here: Workers and
 * their terminals come from `worker_terminals`, extra terminals from
 * `live_terminals`, sessions from `sessions`, runtimes from node offerings and
 * the AI connection probes, local models from the Ollama status rows, and capacity is the #483
 * `MachineCapacity` exactly as built for the rest of the screen.
 *
 * Truth rules this module keeps:
 *
 * - A Worker is a stable identity. It is not the machine, the terminal, the
 *   runtime, the model or the task; each of those is its own field and each can
 *   be absent.
 * - An installed runtime is a capability, not a session. "Claude Code
 *   installed" is one fact; "two Claude Code sessions" is another and is only
 *   ever counted from real sessions.
 * - Local and external intelligence are kept apart. A model that runs in
 *   Ollama on this machine is local. A cloud model reached through a CLI on this
 *   machine executes its CLI here and thinks elsewhere.
 * - Local runtimes have no quota. Nothing on this board shows a percentage for
 *   a local model.
 * - Unknown stays Unknown. A missing reading is null, never zero.
 *
 * Pure: no I/O, no clock. The moment arrives as `nowMs`.
 */

import type { MachineCapacity, MachineMeter } from '@brunel/shared';
import { capacityProfileLabel } from '@brunel/shared';
import type { AiCapacityMeter } from './ai-usage-view';
import { AI_CONNECTIONS_DISPLAY } from './ai-connections-catalog';
import type {
  AiConnectionStatusRow, AiLocalRuntimeStatusRow, ModelOfferingRef, NodeOffering, NodePresence,
  RunningTask, RuntimeOutage, TerminalSessionRow, WorkerOfferingPreference, WorkerRoutingRow,
  WorkerTerminalRow, WorkforceSession,
} from './data';
import { consumerRoleLabel, type AiConnectionRoleBindingRow } from './ai-role-bindings';
import { durationPhrase } from './finished';
import { parseOfferingKey } from './offering-resolve';

// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------

/** Where a Worker's thinking happens, stated only from the offering row. */
export type Intelligence = 'local' | 'cloud' | 'unknown';

/** The four required arrangements of the same board, plus the optional tree. */
export type MachineBoardView = 'worker' | 'runtime' | 'capacity' | 'project' | 'topology';

export const MACHINE_BOARD_VIEWS: readonly { readonly id: MachineBoardView; readonly label: string }[] = [
  { id: 'worker', label: 'By Worker' },
  { id: 'runtime', label: 'By Runtime / AI' },
  { id: 'capacity', label: 'By Capacity' },
  { id: 'project', label: 'By Project' },
  { id: 'topology', label: 'Topology' },
];

/** What a terminal's own state comes to, in five words the owner can scan. */
export type TerminalPhase = 'running' | 'waiting' | 'idle' | 'starting' | 'exited' | 'unknown';

export interface BoardTerminal {
  readonly sessionId: string;
  readonly shortId: string;
  /** The Worker that owns this terminal, or null for a terminal no Worker owns. */
  readonly workerId: string | null;
  readonly workerLabel: string | null;
  readonly status: string | null;
  readonly phase: TerminalPhase;
  readonly phaseLabel: string;
  /** The shell, and the runtime the owning Worker's session is on when there is one. */
  readonly process: string | null;
  readonly runtimeLabel: string | null;
  readonly pid: number | null;
  readonly cwd: string | null;
  readonly lastActivityAt: string | null;
  /** Who holds the keyboard, in words; null when nobody holds it or it is unknown. */
  readonly inputHolder: string | null;
  readonly inputHeldByOwner: boolean;
  /** True when this is what the machine last reported and the machine is out of touch. */
  readonly lastReported: boolean;
}

export interface BoardWorkerRuntime {
  readonly offeringKey: string | null;
  readonly runtimeKey: string | null;
  readonly runtimeLabel: string | null;
  readonly modelKey: string | null;
  readonly providerLabel: string | null;
  readonly intelligence: Intelligence;
  /** One line saying where this Worker executes and where it thinks. */
  readonly intelligenceLine: string;
  /** True when the owner pinned this Worker to an offering on this machine. */
  readonly pinned: boolean;
}

export interface BoardWorker {
  readonly workerId: string;
  readonly number: number;
  readonly name: string;
  readonly label: string;
  readonly status: string;
  readonly active: boolean;
  readonly terminal: BoardTerminal | null;
  readonly sessionId: string | null;
  readonly sessionLifecycle: string | null;
  readonly runtime: BoardWorkerRuntime;
  readonly projectId: string | null;
  readonly projectName: string | null;
  readonly taskId: string | null;
  readonly taskTitle: string | null;
  readonly taskStartedAt: string | null;
  readonly elapsed: string | null;
  readonly worktree: string | null;
  /** What is holding this Worker up, in the owner's words. */
  readonly blocker: string | null;
  /** A capacity or quota warning that applies to what this Worker is using. */
  readonly capacityWarning: string | null;
}

export interface BoardRuntimeConnection {
  readonly connectionKey: string;
  readonly label: string;
  readonly installed: boolean | null;
  readonly authState: 'signed_in' | 'signed_out' | 'unknown' | 'no_auth';
  readonly note: string | null;
  readonly lastProbeAt: string | null;
}

export interface BoardRuntimeModel {
  readonly offeringKey: string;
  readonly modelKey: string;
  readonly intelligence: Intelligence;
  readonly providerLabel: string;
  readonly workersUsing: readonly string[];
}

export interface BoardRuntime {
  readonly runtimeKey: string;
  readonly label: string;
  readonly availability: 'available' | 'unavailable' | 'unknown';
  readonly availabilityLine: string;
  readonly version: string | null;
  readonly connections: readonly BoardRuntimeConnection[];
  readonly models: readonly BoardRuntimeModel[];
  /** Real sessions on this machine using this runtime right now. */
  readonly activeSessions: number;
  readonly workersUsing: readonly string[];
  /** Usage text only when a real reading exists; "Local · no quota" for a local runtime. */
  readonly usage: string | null;
  readonly usageMeterKey: string | null;
  readonly outage: string | null;
  /** Owner roles (Main Overseer, Reviewer) bound to this runtime on this machine. */
  readonly servesRoles: readonly string[];
  readonly idle: boolean;
}

export interface BoardLocalModel {
  readonly name: string;
  readonly installed: boolean;
  readonly loaded: boolean;
  readonly size: string | null;
  readonly processor: string | null;
  readonly contextLength: string | null;
  readonly offered: boolean;
  readonly workersUsing: readonly string[];
  readonly state: 'in_use' | 'loaded_idle' | 'installed_idle' | 'offered_only';
  readonly stateLabel: string;
}

export interface BoardLocalRuntime {
  readonly connectionKey: string;
  readonly label: string;
  readonly reachable: 'reachable' | 'unreachable' | 'unknown';
  readonly version: string | null;
  readonly installedKnown: boolean;
  readonly loadedKnown: boolean;
  readonly models: readonly BoardLocalModel[];
  readonly observedAt: string | null;
  /** Always the same words: local models have no quota and none is shown. */
  readonly quotaLine: string;
}

export interface BoardProject {
  readonly projectId: string;
  readonly name: string;
  readonly worktrees: readonly string[];
  readonly workers: readonly string[];
  readonly tasks: readonly string[];
  /** The owner's standing routing instruction for this project, when it points here. */
  readonly routing: string | null;
  readonly warning: string | null;
}

export interface BoardCapacity {
  readonly profileLabel: string;
  readonly seatsConfigured: number | null;
  readonly seatsActive: number | null;
  readonly seatsFree: number | null;
  readonly seatsText: string;
  readonly reserveMemoryPercent: number;
  readonly reserveGraphicsPercent: number;
  readonly cpuCeilingPercent: number | null;
  readonly graphicsCeilingPercent: number | null;
  readonly guards: MachineCapacity['guards'];
  readonly meters: readonly MachineMeter[];
  readonly stance: MachineCapacity['stance'];
  readonly stanceLabel: string;
  readonly holdBackReason: string | null;
  readonly dispatchLine: string;
  /**
   * How many more Workers the hard limits would let this machine take, or null
   * when that cannot be said truthfully. Only the seat limit is a count; the
   * memory and graphics reserves and the load ceilings are re-judged at each
   * dispatch, so this is never more than the free seats and never a percent.
   */
  readonly safeAdditionalWorkers: number | null;
  readonly safeAdditionalLine: string;
}

export interface BoardSummary {
  readonly nodeId: string;
  readonly name: string;
  readonly presence: MachineCapacity['presence'];
  readonly presenceLabel: string;
  readonly live: boolean;
  readonly versionLine: string;
  readonly profileLabel: string;
  readonly meters: readonly MachineMeter[];
  readonly cpuCount: number | null;
  readonly workersActive: number;
  readonly workersHere: number;
  readonly seatsText: string;
  readonly activeRuns: number | null;
  readonly stance: MachineCapacity['stance'];
  readonly stanceLabel: string;
  readonly holdBackReason: string | null;
  readonly dispatchLine: string;
  readonly lastSeenAt: string | null;
  readonly observedAt: string | null;
  readonly readingAgeLabel: string | null;
  readonly stale: boolean;
  /** Shown above everything when the machine is not in touch. */
  readonly truthNote: string | null;
}

export interface MachineBoardModel {
  readonly summary: BoardSummary;
  readonly workers: readonly BoardWorker[];
  readonly terminals: readonly BoardTerminal[];
  readonly activeTerminalCount: number;
  readonly runtimes: readonly BoardRuntime[];
  readonly localRuntimes: readonly BoardLocalRuntime[];
  readonly projects: readonly BoardProject[];
  readonly capacity: BoardCapacity;
  /** Plain sentences a supervisor can read aloud; the Machinist consumes these. */
  readonly facts: readonly string[];
  /** Whether the extra (non-Worker) terminal list was read, so an absent one is not "none". */
  readonly terminalsComplete: boolean;
}

export interface MachineBoardInput {
  readonly nodeId: string;
  readonly nowMs: number;
  /** The #483 capacity model for this machine, exactly as the rest of the screen built it. */
  readonly machine: MachineCapacity | null;
  readonly node: NodePresence | null;
  readonly workerTerminals: readonly WorkerTerminalRow[];
  readonly workforceSessions: readonly WorkforceSession[];
  /** This machine's `live_terminals`, or null when they were not read. */
  readonly liveTerminals: readonly TerminalSessionRow[] | null;
  readonly nodeOfferings: readonly NodeOffering[];
  readonly modelOfferings: readonly ModelOfferingRef[];
  readonly workerOfferingPreferences: readonly WorkerOfferingPreference[];
  readonly connectionStatuses: readonly AiConnectionStatusRow[];
  readonly localRuntimes: readonly AiLocalRuntimeStatusRow[];
  readonly roleBindings: readonly AiConnectionRoleBindingRow[];
  readonly running: readonly RunningTask[];
  readonly workerRouting: readonly WorkerRoutingRow[];
  readonly outages: readonly RuntimeOutage[];
  readonly meters: readonly AiCapacityMeter[];
  readonly pendingApprovalsByWorker?: ReadonlyMap<string, number>;
  /** Owner-facing runtime names from the launch catalogue, keyed by runtime key. */
  readonly runtimeLabels?: Readonly<Record<string, string>>;
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

const ACTIVE_LIFECYCLES = new Set(['active', 'restored']);
const EXITED_STATUSES = new Set(['closing', 'closed', 'failed']);

function text(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed === undefined || trimmed === '' ? null : trimmed;
}

function uniqueSorted(values: readonly string[]): string[] {
  return [...new Set(values)].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
}

export function shortSessionId(id: string): string {
  return id.replace(/-/g, '').slice(0, 8);
}

export function workerLabel(number: number, name: string | null): string {
  const clean = text(name);
  return clean === null || clean === `Worker ${number}` ? `Worker ${number}` : `Worker ${number} · ${clean}`;
}

/** The owner's name for a runtime key: launch catalogue, then the AI connection catalogue, then the key. */
export function runtimeDisplayLabel(
  runtimeKey: string,
  labels?: Readonly<Record<string, string>>,
): string {
  const stated = text(labels?.[runtimeKey]);
  if (stated !== null) return stated;
  const matches = AI_CONNECTIONS_DISPLAY.filter((row) => row.runtimeKeys.includes(runtimeKey));
  if (matches.length === 1) return matches[0]!.displayLabel;
  if (runtimeKey === 'ollama') return 'Ollama';
  return runtimeKey;
}

function connectionLabel(connectionKey: string): string {
  return AI_CONNECTIONS_DISPLAY.find((row) => row.connectionKey === connectionKey)?.displayLabel ?? connectionKey;
}

function runtimeKeysOfConnection(connectionKey: string): readonly string[] {
  return AI_CONNECTIONS_DISPLAY.find((row) => row.connectionKey === connectionKey)?.runtimeKeys ?? [];
}

function isLocalNoAuth(connectionKey: string): boolean {
  return AI_CONNECTIONS_DISPLAY.find((row) => row.connectionKey === connectionKey)?.connectionMode === 'local_no_auth';
}

export function terminalPhase(status: string | null, closedAt: string | null = null): TerminalPhase {
  if (closedAt !== null) return 'exited';
  if (status === null) return 'unknown';
  if (status === 'running') return 'running';
  if (status === 'waiting_for_input' || status === 'waiting_for_approval') return 'waiting';
  if (status === 'idle' || status === 'open') return 'idle';
  if (status === 'requested') return 'starting';
  if (EXITED_STATUSES.has(status)) return 'exited';
  return 'unknown';
}

const PHASE_LABELS: Readonly<Record<TerminalPhase, string>> = {
  running: 'Running',
  waiting: 'Waiting',
  idle: 'At prompt',
  starting: 'Starting',
  exited: 'Exited',
  unknown: 'Unknown',
};

function phaseLabel(phase: TerminalPhase, status: string | null, lastReported: boolean): string {
  const base = phase === 'waiting'
    ? (status === 'waiting_for_approval' ? 'Waiting for approval' : 'Waiting for input')
    : PHASE_LABELS[phase];
  return lastReported && phase !== 'exited' ? `${base} (last reported)` : base;
}

function inputHolderText(row: {
  readonly input_held: boolean | null;
  readonly input_holder: string | null;
  readonly input_holder_controller_name: string | null;
}): { readonly holder: string | null; readonly owner: boolean } {
  if (row.input_held !== true) return { holder: null, owner: false };
  if (row.input_holder === 'owner_terminal' || row.input_holder === 'owner_composer') {
    return { holder: 'Owner', owner: true };
  }
  const holder = text(row.input_holder_controller_name) ?? text(row.input_holder?.replaceAll('_', ' ')) ?? 'Another controller';
  return { holder, owner: false };
}

function intelligenceOf(offering: ModelOfferingRef | null, location: string | null): Intelligence {
  const where = offering?.execution_location ?? location;
  if (where === 'local') return 'local';
  if (where === 'cloud') return 'cloud';
  return 'unknown';
}

function intelligenceLine(
  intelligence: Intelligence,
  nodeName: string,
  runtimeLabel: string | null,
  providerLabel: string | null,
): string {
  if (runtimeLabel === null) return 'No runtime session recorded for this Worker.';
  if (intelligence === 'local') return `Executes and thinks on ${nodeName} (${runtimeLabel}, local model).`;
  if (intelligence === 'cloud') {
    return `Executes on ${nodeName} through ${runtimeLabel}; the model runs at ${providerLabel ?? 'the provider'}, not on ${nodeName}.`;
  }
  return `Executes on ${nodeName} through ${runtimeLabel}; where its model runs is not recorded.`;
}

// ---------------------------------------------------------------------------
// Builder
// ---------------------------------------------------------------------------

/**
 * Build one machine's board.
 *
 * A Worker belongs to this board when the canonical Worker terminal row puts it
 * on this node, or - with no terminal placement at all - when it holds an
 * active session here. A Worker whose terminal is on another machine is that
 * machine's, whatever an older session row says.
 */
export function buildMachineBoard(input: MachineBoardInput): MachineBoardModel {
  const { nodeId, machine, nowMs } = input;
  const nodeName = machine?.name ?? input.node?.name ?? 'This machine';
  const presence = machine?.presence ?? 'unknown';
  const live = presence === 'connected';
  const labels = input.runtimeLabels;
  const offeringByKey = new Map(input.modelOfferings.map((row) => [row.key, row]));

  // ---- Workers -----------------------------------------------------------
  const terminalRowsHere = input.workerTerminals.filter((row) => row.node_id === nodeId);
  const placedElsewhere = new Set(
    input.workerTerminals.filter((row) => row.node_id !== null && row.node_id !== nodeId).map((row) => row.worker_id),
  );
  const rowByWorker = new Map(input.workerTerminals.map((row) => [row.worker_id, row]));
  const sessionsHere = input.workforceSessions.filter(
    (row) => row.node_id === nodeId && ACTIVE_LIFECYCLES.has(row.lifecycle),
  );
  const sessionOnlyWorkers = uniqueSorted(
    sessionsHere.map((row) => row.worker_id).filter((id) =>
      !placedElsewhere.has(id) && !terminalRowsHere.some((row) => row.worker_id === id)),
  );

  const outageByRuntime = new Map(
    input.outages.filter((row) => row.node_id === nodeId && !row.expired).map((row) => [row.runtime_key, row]),
  );
  const meterFor = (runtimeKey: string | null): AiCapacityMeter | null => {
    if (runtimeKey === null) return null;
    return input.meters.find((meter) => meter.runtime === runtimeKey && meter.nodeId === nodeId)
      ?? input.meters.find((meter) => meter.runtime === runtimeKey && meter.nodeId === null)
      ?? null;
  };

  const buildTerminalFromWorkerRow = (row: WorkerTerminalRow): BoardTerminal | null => {
    if (row.session_id === null) return null;
    const phase = terminalPhase(row.terminal_status, row.closed_at);
    const lastReported = !live;
    const offering = parseOfferingKey(row.offering_key);
    const holder = inputHolderText(row);
    return {
      sessionId: row.session_id,
      shortId: shortSessionId(row.session_id),
      workerId: row.worker_id,
      workerLabel: workerLabel(row.worker_number, row.worker_name),
      status: row.terminal_status,
      phase,
      phaseLabel: phaseLabel(phase, row.terminal_status, lastReported),
      process: text(row.shell),
      runtimeLabel: offering.runtimeKey === null ? null : runtimeDisplayLabel(offering.runtimeKey, labels),
      pid: row.pid,
      cwd: text(row.cwd),
      lastActivityAt: row.last_activity_at,
      inputHolder: holder.holder,
      inputHeldByOwner: holder.owner,
      lastReported,
    };
  };

  const workerRuntime = (workerId: string, offeringKey: string | null): BoardWorkerRuntime => {
    const parsed = parseOfferingKey(offeringKey);
    const offering = offeringKey === null ? null : offeringByKey.get(offeringKey) ?? null;
    const runtimeKey = offering?.runtime_key ?? parsed.runtimeKey;
    const runtimeLabel = runtimeKey === null ? null : runtimeDisplayLabel(runtimeKey, labels);
    const modelKey = offering?.model_key ?? parsed.modelKey;
    const providerLabel = offering?.provider_label ?? null;
    const intelligence = intelligenceOf(offering, parsed.location);
    const pinned = offeringKey !== null && input.workerOfferingPreferences.some(
      (row) => row.worker_id === workerId && row.node_id === nodeId && row.offering_key === offeringKey,
    );
    return {
      offeringKey,
      runtimeKey,
      runtimeLabel,
      modelKey,
      providerLabel,
      intelligence,
      intelligenceLine: intelligenceLine(intelligence, nodeName, runtimeLabel, providerLabel),
      pinned,
    };
  };

  const capacityWarningFor = (runtime: BoardWorkerRuntime): string | null => {
    if (runtime.runtimeKey === null) return null;
    const outage = outageByRuntime.get(runtime.runtimeKey);
    if (outage) return `${runtime.runtimeLabel} reported unavailable here (${outage.reason_code.replaceAll('_', ' ')}).`;
    if (runtime.intelligence === 'local') return null;
    const meter = meterFor(runtime.runtimeKey);
    if (meter === null || !meter.known) return null;
    if (meter.quotaSignal === 'exhausted' || meter.band === 'exhausted' || meter.band === 'hard_exceeded') {
      return `${meter.title}: allowance exhausted.`;
    }
    if (meter.quotaSignal === 'near_limit' || meter.quotaSignal === 'rate_limited'
      || meter.band === 'warn' || meter.band === 'soft_exceeded' || meter.band === 'reserve_breached') {
      return `${meter.title}: ${meter.usedLabel ?? 'near its limit'}.`;
    }
    return null;
  };

  const workers: BoardWorker[] = [];
  for (const row of terminalRowsHere) {
    const session = input.workforceSessions.find((item) => item.id === row.workforce_session_id) ?? null;
    const offeringKey = text(row.offering_key) ?? text(session?.offering_key) ?? null;
    const runtime = workerRuntime(row.worker_id, offeringKey);
    const terminal = buildTerminalFromWorkerRow(row);
    const pending = input.pendingApprovalsByWorker?.get(row.worker_id) ?? 0;
    const terminalStatus = text(row.terminal_status);
    const status = terminalStatus === 'waiting_for_input' || terminalStatus === 'waiting_for_approval'
      ? terminalStatus
      : text(row.worker_status) ?? 'unknown';
    const blocker = pending > 0
      ? `${pending} approval${pending === 1 ? '' : 's'} waiting for you`
      : terminalStatus === 'waiting_for_approval'
        ? 'The tool is asking for permission in the terminal'
        : terminalStatus === 'waiting_for_input'
          ? 'Waiting for input in the terminal'
          : text(row.failure) !== null ? `Terminal failed: ${row.failure}` : null;
    const started = row.task_started_at === null ? Number.NaN : Date.parse(row.task_started_at);
    workers.push({
      workerId: row.worker_id,
      number: row.worker_number,
      name: row.worker_name,
      label: workerLabel(row.worker_number, row.worker_name),
      status,
      active: text(row.task_id) !== null || (terminal !== null && (terminal.phase === 'running' || terminal.phase === 'waiting')),
      terminal,
      sessionId: row.workforce_session_id,
      sessionLifecycle: text(row.lifecycle) ?? session?.lifecycle ?? null,
      runtime,
      projectId: row.project_id ?? session?.project_id ?? null,
      projectName: text(row.project_name),
      taskId: text(row.task_id),
      taskTitle: text(row.task_title),
      taskStartedAt: row.task_started_at,
      elapsed: Number.isNaN(started) ? null : durationPhrase(Math.max(0, nowMs - started)),
      worktree: terminal?.cwd ?? null,
      blocker,
      capacityWarning: capacityWarningFor(runtime),
    });
  }
  for (const workerId of sessionOnlyWorkers) {
    const session = sessionsHere.find((row) => row.worker_id === workerId)!;
    const row = rowByWorker.get(workerId) ?? null;
    const number = row?.worker_number ?? 0;
    const runtime = workerRuntime(workerId, text(session.offering_key));
    const runningTask = input.running.find((task) =>
      task.node_name === nodeName && row !== null && task.worker_number === row.worker_number) ?? null;
    const started = runningTask?.started_at ? Date.parse(runningTask.started_at) : Number.NaN;
    workers.push({
      workerId,
      number,
      name: row?.worker_name ?? 'Worker',
      label: row === null ? 'Worker (not in the Worker list)' : workerLabel(row.worker_number, row.worker_name),
      status: text(row?.worker_status) ?? text(session.status) ?? 'unknown',
      active: runningTask !== null,
      terminal: null,
      sessionId: session.id,
      sessionLifecycle: session.lifecycle,
      runtime,
      projectId: session.project_id,
      projectName: runningTask?.project_name ?? null,
      taskId: runningTask?.task_id ?? null,
      taskTitle: runningTask?.title ?? null,
      taskStartedAt: runningTask?.started_at ?? null,
      elapsed: Number.isNaN(started) ? null : durationPhrase(Math.max(0, nowMs - started)),
      worktree: null,
      blocker: null,
      capacityWarning: capacityWarningFor(runtime),
    });
  }
  workers.sort((left, right) => left.number - right.number);

  // ---- Terminals: Worker terminals first, then any other live terminal, never twice ----
  const terminals: BoardTerminal[] = workers.flatMap((worker) => worker.terminal === null ? [] : [worker.terminal]);
  const seen = new Set(terminals.map((terminal) => terminal.sessionId));
  for (const row of input.liveTerminals ?? []) {
    if (row.node_id !== nodeId || seen.has(row.id)) continue;
    seen.add(row.id);
    const phase = terminalPhase(row.status, row.closed_at);
    const lastReported = !live;
    terminals.push({
      sessionId: row.id,
      shortId: shortSessionId(row.id),
      workerId: null,
      workerLabel: null,
      status: row.status,
      phase,
      phaseLabel: phaseLabel(phase, row.status, lastReported),
      process: text(row.shell) ?? text(row.label),
      runtimeLabel: null,
      pid: row.pid,
      cwd: text(row.cwd),
      lastActivityAt: row.last_activity_at,
      inputHolder: null,
      inputHeldByOwner: false,
      lastReported,
    });
  }
  const activeTerminalCount = terminals.filter((terminal) => terminal.phase !== 'exited' && terminal.phase !== 'unknown').length;

  // ---- Runtimes: capability is one fact, sessions are another ----
  const offeringsHere = input.nodeOfferings
    .filter((row) => row.node_id === nodeId)
    .map((row) => offeringByKey.get(row.offering_key) ?? null)
    .filter((row): row is ModelOfferingRef => row !== null);
  const connectionsHere = input.connectionStatuses.filter((row) => row.node_id === nodeId);
  const localHere = input.localRuntimes.filter((row) => row.node_id === nodeId);
  const runtimeKeys = uniqueSorted([
    ...offeringsHere.map((row) => row.runtime_key),
    ...connectionsHere.flatMap((row) => runtimeKeysOfConnection(row.connection_key)),
    ...localHere.flatMap((row) => runtimeKeysOfConnection(row.connection_key)),
    ...workers.map((worker) => worker.runtime.runtimeKey).filter((key): key is string => key !== null),
  ]);
  const activeWorkerSession = (worker: BoardWorker): boolean =>
    worker.terminal !== null
      ? worker.terminal.phase !== 'exited' && worker.terminal.phase !== 'unknown'
      : worker.sessionLifecycle !== null && ACTIVE_LIFECYCLES.has(worker.sessionLifecycle);

  const runtimes: BoardRuntime[] = runtimeKeys.map((runtimeKey) => {
    const label = runtimeDisplayLabel(runtimeKey, labels);
    const connections: BoardRuntimeConnection[] = connectionsHere
      .filter((row) => runtimeKeysOfConnection(row.connection_key).includes(runtimeKey))
      .map((row) => ({
        connectionKey: row.connection_key,
        label: connectionLabel(row.connection_key),
        installed: row.installed,
        authState: isLocalNoAuth(row.connection_key) ? 'no_auth' : row.auth_state,
        note: text(row.capability_note),
        lastProbeAt: row.last_probe_at,
      }));
    const users = workers.filter((worker) => worker.runtime.runtimeKey === runtimeKey);
    const activeUsers = users.filter(activeWorkerSession);
    const models: BoardRuntimeModel[] = offeringsHere
      .filter((row) => row.runtime_key === runtimeKey)
      .map((row) => ({
        offeringKey: row.key,
        modelKey: row.model_key,
        intelligence: row.execution_location,
        providerLabel: row.provider_label,
        workersUsing: users.filter((worker) => worker.runtime.offeringKey === row.key).map((worker) => worker.label),
      }))
      .sort((left, right) => left.modelKey.localeCompare(right.modelKey));
    const local = localHere.find((row) => runtimeKeysOfConnection(row.connection_key).includes(runtimeKey)) ?? null;
    const allLocal = models.length > 0 && models.every((model) => model.intelligence === 'local');
    const outage = outageByRuntime.get(runtimeKey) ?? null;
    const installedKnown = connections.filter((row) => row.installed !== null);
    const offered = models.length > 0;
    const availability: BoardRuntime['availability'] = outage !== null
      ? 'unavailable'
      : local?.service_state === 'unreachable'
        ? 'unavailable'
        : offered || local?.service_state === 'reachable' || installedKnown.some((row) => row.installed === true)
          ? 'available'
          : installedKnown.length > 0 && installedKnown.every((row) => row.installed === false)
            ? 'unavailable'
            : 'unknown';
    const availabilityLine = outage !== null
      ? `Reported unavailable: ${outage.reason_code.replaceAll('_', ' ')}${outage.available_from ? ` until ${outage.available_from}` : ''}`
      : availability === 'available'
        ? offered ? `Offered here with ${models.length} model${models.length === 1 ? '' : 's'}` : 'Installed here; no model offered yet'
        : availability === 'unavailable'
          ? local?.service_state === 'unreachable' ? 'Service not reachable' : 'Not installed on this machine'
          : 'Not probed on this machine';
    const meter = allLocal ? null : meterFor(runtimeKey);
    const usage = allLocal || (local !== null && !offered)
      ? 'Local · no quota'
      : meter !== null && meter.known
        ? [meter.usedLabel, meter.remainingLabel].filter((part): part is string => part !== null).join(' · ') || null
        : null;
    // Nothing stored under 'ux' is used any more, so it is not listed as a role this runtime serves.
    const servesRoles = input.roleBindings
      .filter((binding) => binding.consumer_role !== 'ux'
        && binding.node_id === nodeId && binding.connection_key !== null
        && binding.external_session_id === null
        && runtimeKeysOfConnection(binding.connection_key).includes(runtimeKey))
      .map((binding) => consumerRoleLabel(binding.consumer_role));
    return {
      runtimeKey,
      label,
      availability,
      availabilityLine,
      version: local?.runtime_version ?? null,
      connections,
      models,
      activeSessions: activeUsers.length,
      workersUsing: users.map((worker) => worker.label),
      usage,
      usageMeterKey: allLocal ? null : meter?.key ?? null,
      outage: outage === null ? null : outage.reason_code.replaceAll('_', ' '),
      servesRoles: uniqueSorted(servesRoles),
      idle: activeUsers.length === 0,
    };
  });

  // ---- Ollama / local models ----
  const localRuntimes: BoardLocalRuntime[] = localHere.map((row) => {
    const runtimeKeysHere = runtimeKeysOfConnection(row.connection_key);
    const offeredLocal = offeringsHere.filter((offering) =>
      offering.execution_location === 'local' && runtimeKeysHere.includes(offering.runtime_key));
    const names = uniqueSorted([
      ...(row.installed_models_known ? row.installed_models.map((model) => model.name) : []),
      ...(row.loaded_models_known ? row.loaded_models.map((model) => model.name) : []),
      ...offeredLocal.map((offering) => offering.model_key),
    ]);
    const models: BoardLocalModel[] = names.map((name) => {
      const installed = row.installed_models.find((model) => model.name === name) ?? null;
      const loaded = row.loaded_models.find((model) => model.name === name) ?? null;
      const workersUsing = workers
        .filter((worker) => worker.runtime.intelligence === 'local'
          && worker.runtime.runtimeKey !== null && runtimeKeysHere.includes(worker.runtime.runtimeKey)
          && worker.runtime.modelKey === name && activeWorkerSession(worker))
        .map((worker) => worker.label);
      const state: BoardLocalModel['state'] = workersUsing.length > 0
        ? 'in_use'
        : loaded !== null ? 'loaded_idle' : installed !== null ? 'installed_idle' : 'offered_only';
      const stateLabel = state === 'in_use'
        ? `In use by ${workersUsing.join(', ')}`
        : state === 'loaded_idle' ? 'Loaded, idle' : state === 'installed_idle' ? 'Installed, not loaded' : 'Offered, not reported installed';
      return {
        name,
        installed: installed !== null,
        loaded: loaded !== null,
        size: loaded?.loadedSize ?? installed?.fileSize ?? null,
        processor: loaded?.processor ?? null,
        contextLength: loaded?.contextLength ?? null,
        offered: offeredLocal.some((offering) => offering.model_key === name),
        workersUsing,
        state,
        stateLabel,
      };
    });
    return {
      connectionKey: row.connection_key,
      label: connectionLabel(row.connection_key),
      reachable: row.service_state,
      version: row.runtime_version,
      installedKnown: row.installed_models_known,
      loadedKnown: row.loaded_models_known,
      models,
      observedAt: row.observed_at,
      quotaLine: 'Local · no quota. Limited only by this machine\'s memory and graphics.',
    };
  });

  // ---- Projects / worktrees ----
  const projectIds = uniqueSorted(workers.map((worker) => worker.projectId).filter((id): id is string => id !== null));
  const projects: BoardProject[] = projectIds.map((projectId) => {
    const members = workers.filter((worker) => worker.projectId === projectId);
    const worktrees = uniqueSorted(members.map((worker) => worker.worktree).filter((cwd): cwd is string => cwd !== null));
    const activeMembers = members.filter((worker) => worker.active);
    const sharedTree = worktrees.find((tree) =>
      activeMembers.filter((worker) => worker.worktree === tree).length > 1) ?? null;
    const routing = input.workerRouting.find((row) => row.project_id === projectId) ?? null;
    const routingHere = routing !== null && (routing.node_name === null || routing.node_name === nodeName);
    return {
      projectId,
      name: members.find((worker) => worker.projectName !== null)?.projectName
        ?? routing?.project_name ?? 'Project (name not visible)',
      worktrees,
      workers: members.map((worker) => worker.label),
      tasks: members.map((worker) => worker.taskTitle).filter((title): title is string => title !== null),
      routing: routing === null
        ? null
        : routingHere
          ? `New work routed to ${workerLabel(routing.worker_number, routing.worker_name)}`
          : `New work routed to ${workerLabel(routing.worker_number, routing.worker_name)} on ${routing.node_name}`,
      warning: sharedTree === null ? null : `Two or more active Workers are in the same worktree: ${sharedTree}`,
    };
  });

  // ---- Capacity (straight from #483) ----
  const seatsConfigured = machine?.seatsConfigured ?? null;
  const seatsFree = machine?.seatsFree ?? null;
  const stance = machine?.stance ?? 'unknown';
  const safeAdditionalWorkers = live && stance === 'accepting' && seatsFree !== null ? seatsFree : live && stance !== 'unknown' ? 0 : null;
  const safeAdditionalLine = safeAdditionalWorkers === null
    ? 'Unknown: the machine is not in touch or its seats and readings are not known.'
    : safeAdditionalWorkers === 0
      ? `None right now: ${machine?.holdBackReason ?? machine?.dispatchLine ?? 'no free seat'}.`
      : `Up to ${safeAdditionalWorkers} more Worker${safeAdditionalWorkers === 1 ? '' : 's'} by the seat limit. Memory and graphics reserves are checked again at each dispatch.`;
  const profileLabel = machine === null ? 'Unknown' : capacityProfileLabel(machine.profile);
  const capacity: BoardCapacity = {
    profileLabel,
    seatsConfigured,
    seatsActive: machine?.seatsActive ?? null,
    seatsFree,
    seatsText: machine?.seatsText ?? 'Unknown',
    reserveMemoryPercent: machine?.limits.reserveMemoryPercent ?? 0,
    reserveGraphicsPercent: machine?.limits.reserveGraphicsPercent ?? 0,
    cpuCeilingPercent: machine?.limits.cpuCeilingPercent ?? null,
    graphicsCeilingPercent: machine?.limits.graphicsCeilingPercent ?? null,
    guards: machine?.guards ?? [],
    meters: machine?.meters ?? [],
    stance,
    stanceLabel: machine?.stanceLabel ?? 'Unknown',
    holdBackReason: machine?.holdBackReason ?? null,
    dispatchLine: machine?.dispatchLine ?? 'Nothing is known about this machine\'s capacity.',
    safeAdditionalWorkers,
    safeAdditionalLine,
  };

  // ---- Summary ----
  const truthNote = presence === 'disconnected'
    ? `${nodeName} is out of touch. Everything below is what it last reported, not what it is doing now.`
    : presence === 'stale'
      ? `${nodeName}'s heartbeat is late. States below may already have changed.`
      : presence === 'unknown'
        ? `Brunel has no presence reading for ${nodeName}.`
        : null;
  const summary: BoardSummary = {
    nodeId,
    name: nodeName,
    presence,
    presenceLabel: machine?.presenceLabel ?? 'Unknown',
    live,
    versionLine: machine?.versionLine ?? 'Version unknown',
    profileLabel,
    meters: machine?.meters ?? [],
    cpuCount: machine?.cpuCount ?? null,
    workersActive: workers.filter((worker) => worker.active).length,
    workersHere: workers.length,
    seatsText: capacity.seatsText,
    activeRuns: machine?.activeRuns ?? null,
    stance,
    stanceLabel: capacity.stanceLabel,
    holdBackReason: capacity.holdBackReason,
    dispatchLine: capacity.dispatchLine,
    lastSeenAt: machine?.lastSeenAt ?? input.node?.last_seen_at ?? null,
    observedAt: machine?.observedAt ?? null,
    readingAgeLabel: machine?.readingAgeLabel ?? null,
    stale: machine?.stale ?? true,
    truthNote,
  };

  const model: Omit<MachineBoardModel, 'facts'> = {
    summary,
    workers,
    terminals,
    activeTerminalCount,
    runtimes,
    localRuntimes,
    projects,
    capacity,
    terminalsComplete: input.liveTerminals !== null,
  };
  return { ...model, facts: machineBoardFacts(model) };
}

/**
 * The board in plain sentences - the same topology the Machinist reads, so a
 * supervisor never keeps a second picture of a machine. Only facts the board
 * itself holds; nothing here is estimated.
 */
export function machineBoardFacts(board: Omit<MachineBoardModel, 'facts'>): readonly string[] {
  const facts: string[] = [];
  const name = board.summary.name;
  if (!board.summary.live) {
    facts.push(board.summary.truthNote ?? `${name} is not in touch.`);
  }
  // Seats in use are the #483 figure, never re-counted here.
  if (board.capacity.seatsConfigured !== null && board.capacity.seatsActive !== null) {
    facts.push(`${name} has ${board.capacity.seatsActive} of ${board.capacity.seatsConfigured} Worker seats active.`);
  }
  for (const runtime of board.runtimes) {
    const users = runtime.workersUsing;
    if (users.length === 2) facts.push(`${users[0]} and ${users[1]} are both using ${runtime.label}.`);
    if (users.length > 2) facts.push(`${users.slice(0, -1).join(', ')} and ${users[users.length - 1]} are all using ${runtime.label}.`);
  }
  for (const local of board.localRuntimes) {
    for (const model of local.models) {
      if (model.state === 'loaded_idle') facts.push(`${model.name} is loaded but idle on ${name}.`);
    }
  }
  if (board.capacity.holdBackReason !== null) {
    facts.push(`${name} is held back: ${board.capacity.holdBackReason}.`);
  } else if (board.capacity.safeAdditionalWorkers !== null && board.capacity.safeAdditionalWorkers > 0) {
    facts.push(`${name} can take up to ${board.capacity.safeAdditionalWorkers} more Worker${board.capacity.safeAdditionalWorkers === 1 ? '' : 's'} by its seat limit.`);
  }
  return facts;
}

// ---------------------------------------------------------------------------
// Filtering
// ---------------------------------------------------------------------------

export interface MachineBoardFilters {
  readonly activeOnly: boolean;
  /** A runtime key, or null for every runtime. */
  readonly runtime: string | null;
  /** A project id, or null for every project. */
  readonly project: string | null;
  readonly intelligence: Intelligence | null;
  readonly model: string | null;
  /** Worker availability: busy (has work), available (idle), or blocked. */
  readonly availability: 'busy' | 'available' | 'blocked' | null;
}

export const DEFAULT_BOARD_FILTERS: MachineBoardFilters = {
  activeOnly: false, runtime: null, project: null, intelligence: null, model: null, availability: null,
};

/** The Workers a set of filters keeps, in the same order. Filtering only hides; it never re-derives. */
export function filterBoardWorkers(
  workers: readonly BoardWorker[],
  filters: MachineBoardFilters,
): readonly BoardWorker[] {
  return workers.filter((worker) => {
    if (filters.activeOnly && !worker.active) return false;
    if (filters.runtime !== null && worker.runtime.runtimeKey !== filters.runtime) return false;
    if (filters.project !== null && worker.projectId !== filters.project) return false;
    if (filters.intelligence !== null && worker.runtime.intelligence !== filters.intelligence) return false;
    if (filters.model !== null && worker.runtime.modelKey !== filters.model) return false;
    if (filters.availability === 'blocked' && worker.blocker === null) return false;
    if (filters.availability === 'busy' && !worker.active) return false;
    if (filters.availability === 'available' && (worker.active || worker.blocker !== null)) return false;
    return true;
  });
}

/** How many filters are narrowing the board, for the "clear" affordance. */
export function activeFilterCount(filters: MachineBoardFilters): number {
  return (filters.activeOnly ? 1 : 0)
    + [filters.runtime, filters.project, filters.intelligence, filters.model, filters.availability]
      .filter((value) => value !== null).length;
}

// ---------------------------------------------------------------------------
// Direct links
// ---------------------------------------------------------------------------

const HASH_PREFIX = '#machine/';

/** The bookmarkable link fragment for one machine's board. */
export function machineBoardHash(nodeId: string): string {
  return `${HASH_PREFIX}${encodeURIComponent(nodeId)}`;
}

/** The machine a link fragment names, or null when it names none. */
export function parseMachineBoardHash(hash: string | null | undefined): string | null {
  if (!hash || !hash.startsWith(HASH_PREFIX)) return null;
  const raw = hash.slice(HASH_PREFIX.length);
  if (raw === '') return null;
  try {
    return decodeURIComponent(raw);
  } catch {
    return null;
  }
}
